import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Send,
  Loader2,
  CheckCircle2,
  User,
  Globe,
  Briefcase,
  FileWarning,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTIFY_WHATSAPP = "19548059212";

const buildNotificationMessage = (form) => {
  const visaLabels = {
    turismo: "Turismo (B1/B2)",
    negocio: "Negocio",
    turismo_negocio: "Turismo y Negocio",
  };
  const destinationLabels = {
    estados_unidos: "Estados Unidos",
    canada: "Canadá",
    mexico: "México",
  };
  const yesNo = (v) => (v ? "Sí" : "No");
  const lines = [
    "📩 *Nuevo caso enviado desde Divídelo*",
    "",
    "*Agente*",
    `• Nombre: ${form.agent_name}`,
    `• Teléfono: ${form.agent_phone}`,
    `• País: ${form.agent_country}`,
    `• Email: ${form.agent_email}`,
    "",
    "*Cliente*",
    `• Nombre: ${form.client_full_name}`,
    `• Teléfono: ${form.client_phone}`,
    form.client_email ? `• Email: ${form.client_email}` : null,
    `• País de residencia: ${form.client_country_of_residence}`,
    `• Viajeros: ${form.num_travelers}`,
    `• Grupo familiar: ${yesNo(form.is_family_group)}`,
    "",
    "*Visa y destino*",
    `• Tipo: ${visaLabels[form.visa_type] || form.visa_type}`,
    `• Destino: ${destinationLabels[form.destination_country] || form.destination_country}`,
    "",
    "*Historial*",
    `• Primera vez: ${yesNo(form.is_first_time)}`,
    `• Rechazos previos: ${yesNo(form.has_previous_rejections)}${form.has_previous_rejections && form.rejections_detail ? ` — ${form.rejections_detail}` : ""}`,
    `• Deportación: ${yesNo(form.has_deportation)}${form.has_deportation && form.deportation_detail ? ` — ${form.deportation_detail}` : ""}`,
    `• Visa vigente: ${yesNo(form.has_valid_visa)}${form.has_valid_visa && form.valid_visa_detail ? ` — ${form.valid_visa_detail}` : ""}`,
    form.notes ? `\n*Notas*\n${form.notes}` : null,
  ].filter(Boolean);
  return lines.join("\n");
};

const initialForm = {
  agent_name: "",
  agent_phone: "",
  agent_country: "",
  agent_email: "",
  client_full_name: "",
  client_email: "",
  client_phone: "",
  client_country_of_residence: "",
  visa_type: "turismo",
  destination_country: "estados_unidos",
  is_first_time: false,
  has_previous_rejections: false,
  rejections_detail: "",
  has_deportation: false,
  deportation_detail: "",
  has_valid_visa: false,
  valid_visa_detail: "",
  num_travelers: 1,
  is_family_group: false,
  notes: "",
};

const AgentReferralFormSection = ({ compact = false }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.agent_name ||
      !form.agent_phone ||
      !form.agent_country ||
      !form.agent_email ||
      !form.client_full_name ||
      !form.client_phone ||
      !form.client_country_of_residence
    ) {
      toast.error("Por favor completa los campos obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/agent-referrals/submit`, {
        ...form,
        num_travelers: Number(form.num_travelers) || 1,
      });
      // Open WhatsApp with full case info to notify the team
      const waUrl = `https://wa.me/${NOTIFY_WHATSAPP}?text=${encodeURIComponent(buildNotificationMessage(form))}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setSubmitted(true);
      toast.success("¡Información enviada! Se abrió WhatsApp para notificar al equipo.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "No pudimos enviar la información. Intenta nuevamente.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl mx-auto text-center bg-white rounded-2xl shadow-xl border-2 border-emerald-300 p-10"
        data-testid="agent-referral-success"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-navy-900 mb-2">
          ¡Información recibida!
        </h3>
        <p className="text-navy-600 text-sm mb-6">
          Nuestro equipo revisará el caso y contactará al cliente y al agente en las próximas horas hábiles.
        </p>
        <Button
          onClick={() => {
            setForm(initialForm);
            setSubmitted(false);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="agent-referral-new-btn"
        >
          Enviar otro caso
        </Button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`max-w-3xl mx-auto space-y-8 bg-white rounded-2xl shadow-xl border border-navy-100 ${compact ? "p-6" : "p-6 sm:p-10"}`}
      data-testid="agent-referral-form"
    >
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-bold text-navy-900">
            Datos del agente que envía
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="agent_name">Nombre del agente *</Label>
            <Input id="agent_name" value={form.agent_name} onChange={(e) => update("agent_name", e.target.value)} required data-testid="agent-name-input" />
          </div>
          <div>
            <Label htmlFor="agent_phone">Teléfono / WhatsApp *</Label>
            <Input id="agent_phone" value={form.agent_phone} onChange={(e) => update("agent_phone", e.target.value)} required placeholder="+1..." data-testid="agent-phone-input" />
          </div>
          <div>
            <Label htmlFor="agent_country">País del agente *</Label>
            <Input id="agent_country" value={form.agent_country} onChange={(e) => update("agent_country", e.target.value)} required data-testid="agent-country-input" />
          </div>
          <div>
            <Label htmlFor="agent_email">Email del agente *</Label>
            <Input id="agent_email" type="email" value={form.agent_email} onChange={(e) => update("agent_email", e.target.value)} required data-testid="agent-email-input" />
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-blue-600" />
          <h3 className="font-heading text-lg font-bold text-navy-900">Datos del cliente</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="client_full_name">Nombre completo del cliente *</Label>
            <Input id="client_full_name" value={form.client_full_name} onChange={(e) => update("client_full_name", e.target.value)} required data-testid="client-name-input" />
          </div>
          <div>
            <Label htmlFor="client_phone">Teléfono / WhatsApp *</Label>
            <Input id="client_phone" value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} required placeholder="+..." data-testid="client-phone-input" />
          </div>
          <div>
            <Label htmlFor="client_email">Email del cliente</Label>
            <Input id="client_email" type="email" value={form.client_email} onChange={(e) => update("client_email", e.target.value)} data-testid="client-email-input" />
          </div>
          <div>
            <Label htmlFor="client_country_of_residence">País de residencia *</Label>
            <Input id="client_country_of_residence" value={form.client_country_of_residence} onChange={(e) => update("client_country_of_residence", e.target.value)} required data-testid="client-country-input" />
          </div>
          <div>
            <Label htmlFor="num_travelers">Número de viajeros</Label>
            <Input id="num_travelers" type="number" min="1" max="20" value={form.num_travelers} onChange={(e) => update("num_travelers", e.target.value)} data-testid="num-travelers-input" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Checkbox id="is_family_group" checked={form.is_family_group} onCheckedChange={(v) => update("is_family_group", !!v)} data-testid="is-family-checkbox" />
          <Label htmlFor="is_family_group" className="cursor-pointer">
            Es un grupo familiar (núcleo familiar directo)
          </Label>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-amber-600" />
          <h3 className="font-heading text-lg font-bold text-navy-900">Tipo de visa y destino</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="mb-2 block">Tipo de visa *</Label>
            <RadioGroup value={form.visa_type} onValueChange={(v) => update("visa_type", v)} data-testid="visa-type-group">
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="turismo" id="visa_turismo" />
                <Label htmlFor="visa_turismo" className="cursor-pointer">Turismo (B1/B2)</Label>
              </div>
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="negocio" id="visa_negocio" />
                <Label htmlFor="visa_negocio" className="cursor-pointer">Negocio</Label>
              </div>
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="turismo_negocio" id="visa_ambos" />
                <Label htmlFor="visa_ambos" className="cursor-pointer">Turismo y Negocio</Label>
              </div>
            </RadioGroup>
          </div>
          <div>
            <Label className="mb-2 block">País destino *</Label>
            <RadioGroup value={form.destination_country} onValueChange={(v) => update("destination_country", v)} data-testid="destination-country-group">
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="estados_unidos" id="dest_us" />
                <Label htmlFor="dest_us" className="cursor-pointer">Estados Unidos</Label>
              </div>
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="canada" id="dest_ca" />
                <Label htmlFor="dest_ca" className="cursor-pointer">Canadá</Label>
              </div>
              <div className="flex items-center gap-2 py-1.5">
                <RadioGroupItem value="mexico" id="dest_mx" />
                <Label htmlFor="dest_mx" className="cursor-pointer">México</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <FileWarning className="w-5 h-5 text-rose-600" />
          <h3 className="font-heading text-lg font-bold text-navy-900">Historial migratorio</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox id="is_first_time" checked={form.is_first_time} onCheckedChange={(v) => update("is_first_time", !!v)} data-testid="first-time-checkbox" />
            <Label htmlFor="is_first_time" className="cursor-pointer">
              Es la primera vez que solicita esta visa
            </Label>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Checkbox id="has_previous_rejections" checked={form.has_previous_rejections} onCheckedChange={(v) => update("has_previous_rejections", !!v)} data-testid="rejections-checkbox" />
              <Label htmlFor="has_previous_rejections" className="cursor-pointer">
                Ha tenido rechazos previos
              </Label>
            </div>
            {form.has_previous_rejections && (
              <Textarea className="mt-2" placeholder="¿Cuántos rechazos? ¿En qué años? ¿Motivos, si los recuerda?" value={form.rejections_detail} onChange={(e) => update("rejections_detail", e.target.value)} data-testid="rejections-detail-textarea" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Checkbox id="has_deportation" checked={form.has_deportation} onCheckedChange={(v) => update("has_deportation", !!v)} data-testid="deportation-checkbox" />
              <Label htmlFor="has_deportation" className="cursor-pointer">
                Ha sido deportado(a) previamente
              </Label>
            </div>
            {form.has_deportation && (
              <Textarea className="mt-2" placeholder="Detalles sobre la deportación (país, año, motivo)" value={form.deportation_detail} onChange={(e) => update("deportation_detail", e.target.value)} data-testid="deportation-detail-textarea" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Checkbox id="has_valid_visa" checked={form.has_valid_visa} onCheckedChange={(v) => update("has_valid_visa", !!v)} data-testid="valid-visa-checkbox" />
              <Label htmlFor="has_valid_visa" className="cursor-pointer">
                Ya tiene visa vigente
              </Label>
            </div>
            {form.has_valid_visa && (
              <Textarea className="mt-2" placeholder="Detalles (tipo de visa, país, vigencia)" value={form.valid_visa_detail} onChange={(e) => update("valid_visa_detail", e.target.value)} data-testid="valid-visa-detail-textarea" />
            )}
          </div>
        </div>
      </section>

      <section>
        <Label htmlFor="notes">Notas adicionales</Label>
        <Textarea id="notes" rows={4} placeholder="Cualquier información adicional relevante (motivo del viaje, ocupación, disponibilidad, etc.)" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="notes-textarea" />
      </section>

      <div className="pt-4 border-t border-navy-100">
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90 text-white font-bold py-6 text-base"
          data-testid="agent-referral-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" /> Enviar información al equipo
            </>
          )}
        </Button>
        <p className="text-center text-xs text-navy-500 mt-3">
          Al enviar, aceptas que el equipo de Divídelo contacte tanto al cliente como al agente para iniciar el proceso.
        </p>
      </div>
    </form>
  );
};

export default AgentReferralFormSection;
